import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';
import { authPersistence } from '../../services/authPersistence';
import { apiClient } from '../../services/apiClient';
import { logWarning } from '../../utils/errorLogger';
import { performUserMigration } from '../../utils/authMigration';
import { clearNotifications } from './uiSlice';

export interface User {
  id: string;
  email?: string;
  phone?: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'host' | 'guest' | 'usher';
  roles?: string[]; // Array of all roles user has
  active_role?: string; // Currently active role (from token)
  is_active: boolean;
  created_at: string;
  preferred_language: string;
  get_full_name?: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  access_token_expires_at: string;
  refresh_token_expires_at?: string;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  sessionId: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  loginType: 'email' | 'otp' | null;
  rememberMe: boolean;
  sessionChecked: boolean;
  isVerifying: boolean;
  lastVerified: string | null;
  verificationFailed: boolean;
}

const initialState: AuthState = {
  user: null,
  tokens: null,
  sessionId: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  loginType: null,
  rememberMe: false,
  sessionChecked: false,
  isVerifying: false,
  lastVerified: null,
  verificationFailed: false,
};

// Async thunks for authentication
export const loginWithEmail = createAsyncThunk(
  'auth/loginWithEmail',
  async (credentials: { email: string; password: string; rememberMe?: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const response = await authAPI.loginWithEmail(credentials);
      const result = { 
        ...response.data.data, 
        rememberMe: credentials.rememberMe || false 
      };
      // Clear UI notifications from previous session
      dispatch(clearNotifications());
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Login failed');
    }
  }
);

// Check and restore session from stored data
export const checkStoredSession = createAsyncThunk(
  'auth/checkStoredSession',
  async (_, { rejectWithValue }) => {
    try {
      // ALWAYS check cookies first via API (works across tabs)
      // Don't require stored user data - cookies are the source of truth
      // This fixes the issue where new tabs can't access sessionStorage
      try {
        const profileResponse = await authAPI.getProfile();
        const verifiedUser = profileResponse.data.data;
        
        // Convert verified user to User type
        const restoredUser: User = {
          id: verifiedUser.id,
          email: verifiedUser.email || undefined,
          first_name: verifiedUser.first_name,
          last_name: verifiedUser.last_name,
          role: verifiedUser.role as 'admin' | 'host' | 'guest' | 'usher',
          roles: verifiedUser.roles || [verifiedUser.role],
          active_role: verifiedUser.active_role || verifiedUser.role,
          is_active: verifiedUser.is_active,
          created_at: verifiedUser.created_at || new Date().toISOString(),
          preferred_language: verifiedUser.preferred_language || 'en',
        };

        // Get remember_me from cookie (not localStorage)
        const rememberMe = authPersistence.getRememberMePreference();

        // Store the restored user data in localStorage (accessible across tabs)
        // This ensures future tab opens have the data available
        authPersistence.storeAuthData({
          access_token: '', // Tokens are in httpOnly cookies
          refresh_token: '', // Tokens are in httpOnly cookies
          token_type: 'Bearer',
          access_token_expires_at: '', // Will be updated from API if needed
          user: {
            id: restoredUser.id,
            first_name: restoredUser.first_name,
            last_name: restoredUser.last_name,
            role: restoredUser.role,
            roles: restoredUser.roles,
            active_role: restoredUser.active_role,
            is_active: restoredUser.is_active,
          },
          remember_me: rememberMe,
          last_activity: new Date().toISOString(),
        });

        // Tokens are in cookies, not in state
        return {
          user: restoredUser,
          tokens: null, // Tokens are in httpOnly cookies
          rememberMe: rememberMe,
          loginType: restoredUser.email ? 'email' : 'otp'
        };
      } catch (profileError: any) {
        // Handle 403 (Forbidden) and 401 (Unauthorized) gracefully - it just means "no session"
        // Don't log this as an error when user is not authenticated (expected on login page)
        const status = profileError.response?.status;
        
        if (status === 403 || status === 401) {
          // User is not authenticated - this is expected on login page
          // Don't log as error, just return rejection
          const storedData = authPersistence.getStoredAuthData();
          
          if (storedData && storedData.user) {
            // We have stored data but cookies failed - session expired on backend
            // Clear stored data
            authPersistence.clearAuthData();
          }
          
          return rejectWithValue('No active session');
        }
        
        // For other errors (network, 500, etc.), handle normally
        // Profile call failed - cookies are invalid or expired
        // Check if we have stored data as fallback (for offline scenarios)
        const storedData = authPersistence.getStoredAuthData();
        
        if (storedData && storedData.user) {
          // We have stored data but cookies failed - session expired on backend
          // Clear stored data and reject
          authPersistence.clearAuthData();
          return rejectWithValue('Session expired or invalid');
        }
        
        // No stored data and cookies are invalid - user needs to log in
        return rejectWithValue('Session expired or invalid');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to restore session');
    }
  }
);

// Auto refresh token when needed
export const autoRefreshToken = createAsyncThunk(
  'auth/autoRefreshToken',
  async (_, { rejectWithValue }) => {
    try {
      // Refresh token is now in httpOnly cookie
      // Just call the refresh endpoint - token is in cookie
      const response = await authAPI.refreshToken();
      const newTokens = response.data.data;
      
      // Update last activity (tokens are in cookies, updated by backend)
      authPersistence.updateTokens(
        newTokens.access_token || '',
        newTokens.refresh_token || '',
        newTokens.access_token_expires_at
      );
      
      return newTokens;
    } catch (error: any) {
      // If refresh fails, clear session
      authPersistence.clearAuthData();
      return rejectWithValue(error.response?.data?.error?.message || 'Token refresh failed');
    }
  }
);

export const registerHost = createAsyncThunk(
  'auth/registerHost',
  async (hostData: {
    email: string;
    phone: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.registerHost(hostData);
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Registration failed');
    }
  }
);

export const requestOTP = createAsyncThunk(
  'auth/requestOTP',
  async (guestData: { phone: string; country_code?: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.requestOTP(guestData);
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'OTP request failed');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (otpData: { phone: string; otp_code: string; country_code?: string; remember_me?: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const response = await authAPI.verifyOTP(otpData);
      const result = response.data.data; // Extract the data from APIResponse
      // Clear UI notifications from previous session
      dispatch(clearNotifications());
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'OTP verification failed');
    }
  }
);

export const requestAdminHostOTP = createAsyncThunk(
  'auth/requestAdminHostOTP',
  async (adminHostData: { phone: string; country_code?: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.requestAdminHostOTP(adminHostData);
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'OTP request failed');
    }
  }
);

export const verifyAdminHostOTP = createAsyncThunk(
  'auth/verifyAdminHostOTP',
  async (otpData: { phone: string; otp_code: string; country_code?: string; remember_me?: boolean; login_as_role?: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await authAPI.verifyAdminHostOTP(otpData);
      const result = response.data.data; // Extract the data from APIResponse
      // Clear UI notifications from previous session
      dispatch(clearNotifications());
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'OTP verification failed');
    }
  }
);

export const verifyUsherOTP = createAsyncThunk(
  'auth/verifyUsherOTP',
  async (otpData: { phone: string; otp_code: string; remember_me?: boolean; invitation_token?: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiClient.post('/auth/usher/otp/verify/', otpData);
      const result = response.data.data; // Extract the data from APIResponse
      // Clear UI notifications from previous session
      dispatch(clearNotifications());
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'OTP verification failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      // Refresh token is now in httpOnly cookie, so we just call the endpoint
      const response = await authAPI.refreshToken();
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Token refresh failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (allDevices: boolean = false, { rejectWithValue }) => {
    try {
      await authAPI.logout(allDevices);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Logout failed');
    }
  }
);

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getProfile();
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch profile');
    }
  }
);

export const requestPasswordResetOTP = createAsyncThunk(
  'auth/requestPasswordResetOTP',
  async (resetData: { phone: string; country_code?: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.requestPasswordReset(resetData);
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to request password reset';
      
      if (errorData) {
        // Handle object error structure
        if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.error.details) {
            errorMessage = typeof errorData.error.details === 'string' 
              ? errorData.error.details 
              : 'Failed to request password reset';
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyPasswordResetOTPOnly = createAsyncThunk(
  'auth/verifyPasswordResetOTPOnly',
  async (verifyData: {
    phone: string;
    country_code?: string;
    otp_code: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyPasswordResetOTP(verifyData);
      return response.data.data; // Extract the data from APIResponse
    } catch (error: any) {
      // Extract error message from various possible formats
      const responseData = error.response?.data;
      let errorMessage = 'Failed to verify OTP';
      
      if (responseData) {
        // Check for error.message format
        if (responseData.error?.message) {
          errorMessage = responseData.error.message;
        }
        // Check for error.details format (validation errors)
        else if (responseData.error?.details) {
          const details = responseData.error.details;
          if (typeof details === 'string') {
            errorMessage = details;
          } else if (details.non_field_errors) {
            errorMessage = Array.isArray(details.non_field_errors) 
              ? details.non_field_errors[0] 
              : details.non_field_errors;
          } else if (typeof details === 'object') {
            // Extract first error message from details object
            const firstError = Object.values(details)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError);
          }
        }
        // Check for non_field_errors at root level (DRF format)
        else if (responseData.non_field_errors) {
          errorMessage = Array.isArray(responseData.non_field_errors) 
            ? responseData.non_field_errors[0] 
            : responseData.non_field_errors;
        }
        // Check for direct message
        else if (responseData.message) {
          errorMessage = responseData.message;
        }
        // Check for direct error string
        else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyPasswordResetOTP = createAsyncThunk(
  'auth/verifyPasswordResetOTP',
  async (verifyData: {
    phone: string;
    country_code?: string;
    otp_code: string;
    new_password: string;
    new_password_confirm: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyPasswordReset(verifyData);
      return response.data; // Extract the full response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to reset password');
    }
  }
);

// Verify user data with backend (with debouncing and race condition prevention)
let verificationInProgress = false;
let lastVerificationTime = 0;
const VERIFICATION_DEBOUNCE_MS = 2000; // 2 seconds

export const verifyUserData = createAsyncThunk(
  'auth/verifyUserData',
  async (_, { getState, rejectWithValue }) => {
    // Prevent race conditions - skip if already verifying
    if (verificationInProgress) {
      const state = getState() as { auth: AuthState };
      return state.auth.user;
    }

    // Debounce - skip if called too recently
    const now = Date.now();
    if (now - lastVerificationTime < VERIFICATION_DEBOUNCE_MS) {
      const state = getState() as { auth: AuthState };
      return state.auth.user;
    }

    verificationInProgress = true;
    lastVerificationTime = now;

    try {
      const state = getState() as { auth: AuthState };
      if (!state.auth.isAuthenticated || !state.auth.user || !state.auth.tokens) {
        verificationInProgress = false;
        return rejectWithValue('Not authenticated');
      }

      // Create timeout promise (10 seconds)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Verification timeout')), 10000);
      });

      // Fetch profile with timeout
      const profilePromise = authAPI.getProfile();
      const profileResponse = await Promise.race([profilePromise, timeoutPromise]);
      
      const verifiedUser = profileResponse.data.data;
      
      // Validate user structure
      if (!verifiedUser || !verifiedUser.id || !verifiedUser.role) {
        logWarning('Invalid user data from backend');
        verificationInProgress = false;
        return state.auth.user;
      }

      verificationInProgress = false;
      return verifiedUser;
    } catch (error: any) {
      verificationInProgress = false;
      
      // Handle different error types
      const status = error.response?.status;
      
      if (status === 401) {
        // User logged out on backend
        return rejectWithValue('UNAUTHORIZED');
      } else if (status === 403) {
        // User deactivated
        return rejectWithValue('FORBIDDEN');
      } else if (error.message === 'Verification timeout') {
        // Timeout - don't fail, just return current user
        const state = getState() as { auth: AuthState };
        return state.auth.user;
      } else {
        // Network error or other issues - don't fail, just return current user
        const state = getState() as { auth: AuthState };
        return state.auth.user;
      }
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.tokens = null;
      state.sessionId = null;
      state.isAuthenticated = false;
      state.loginType = null;
      state.error = null;
      state.loading = false;
      state.rememberMe = false;
      state.sessionChecked = true;
      state.isVerifying = false;
      state.lastVerified = null;
      state.verificationFailed = false;
      
      // Clear persisted data
      authPersistence.clearAuthData();
      
      // Clear API cache to prevent showing stale data from previous user
      apiClient.invalidateCache();
    },
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      // Tokens are now in httpOnly cookies, not stored in Redux
      // Keep this for backward compatibility during migration
      state.tokens = action.payload;
      
      // Don't update stored tokens - they're in cookies now
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },
    updateLastActivity: (state) => {
      if (state.isAuthenticated) {
        authPersistence.updateLastActivity();
      }
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      
      // Update localStorage with new user data (tokens are in cookies)
      authPersistence.storeAuthData({
        access_token: '', // Tokens are in httpOnly cookies
        refresh_token: '', // Tokens are in httpOnly cookies
        token_type: 'Bearer',
        access_token_expires_at: '', // Will be updated from API if needed
        user: {
          id: action.payload.id,
          email: action.payload.email || '',
          first_name: action.payload.first_name,
          last_name: action.payload.last_name,
          role: action.payload.role,
          roles: action.payload.roles,
          active_role: action.payload.active_role || action.payload.role,
          is_active: action.payload.is_active,
        },
        remember_me: state.rememberMe,
        last_activity: new Date().toISOString(),
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Login with email
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.loading = false;
        // Extract roles and active_role from response if available
        const userData = {
          ...action.payload.user,
          roles: action.payload.available_roles || [action.payload.user.role],
          active_role: action.payload.active_role || action.payload.user.role,
        };
        state.user = userData;
        // Tokens are now in httpOnly cookies, not in response body
        state.tokens = null;
        state.isAuthenticated = true;
        state.loginType = 'email';
        state.rememberMe = action.payload.rememberMe || false;
        state.sessionChecked = true;
        state.error = null;
        
        // Clear API cache when new user logs in to prevent seeing previous user's data
        apiClient.invalidateCache();
        
        // Store authentication data (without tokens - they're in cookies)
        authPersistence.storeAuthData({
          access_token: '', // Tokens are in httpOnly cookies
          refresh_token: '', // Tokens are in httpOnly cookies
          token_type: 'Bearer',
          access_token_expires_at: '', // Will be updated from API if needed
          user: {
            ...userData,
            email: userData.email || '',
          },
          remember_me: action.payload.rememberMe || false,
          last_activity: new Date().toISOString(),
        });
        
        // Perform migration cleanup for existing users with old tokens
        performUserMigration();
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Register host
      .addCase(registerHost.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerHost.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.loginType = 'email';
        state.error = null;
      })
      .addCase(registerHost.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Request OTP
      .addCase(requestOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        // Extract roles and active_role from response if available
        const userData = {
          ...action.payload.user,
          roles: action.payload.available_roles || [action.payload.user.role],
          active_role: action.payload.active_role || action.payload.user.role,
        };
        state.user = userData;
        // Tokens are now in httpOnly cookies, not in response body
        state.tokens = null;
        state.sessionId = action.payload.sessionId || null;
        state.isAuthenticated = true;
        state.loginType = 'otp';
        state.rememberMe = action.payload.rememberMe || false;
        state.sessionChecked = true;
        state.error = null;
        
        // Clear API cache when new user logs in to prevent seeing previous user's data
        apiClient.invalidateCache();
        
        // Store authentication data (without tokens - they're in cookies)
        authPersistence.storeAuthData({
          access_token: '', // Tokens are in httpOnly cookies
          refresh_token: '', // Tokens are in httpOnly cookies
          token_type: 'Bearer',
          access_token_expires_at: '', // Will be updated from API if needed
          user: {
            ...userData,
            email: userData.email || '',
          },
          remember_me: action.payload.rememberMe || false,
          last_activity: new Date().toISOString(),
          session_id: action.payload.sessionId,
        });
        
        // Perform migration cleanup for existing users with old tokens
        performUserMigration();
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Request Admin/Host OTP
      .addCase(requestAdminHostOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestAdminHostOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestAdminHostOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Verify Admin/Host OTP
      .addCase(verifyAdminHostOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAdminHostOTP.fulfilled, (state, action) => {
        state.loading = false;
        // Extract roles and active_role from response if available
        const userData = {
          ...action.payload.user,
          roles: action.payload.available_roles || [action.payload.user.role],
          active_role: action.payload.active_role || action.payload.user.role,
        };
        state.user = userData;
        // Tokens are now in httpOnly cookies, not in response body
        state.tokens = null;
        state.isAuthenticated = true;
        state.loginType = 'otp';
        state.rememberMe = action.payload.rememberMe || false;
        state.sessionChecked = true;
        state.error = null;
        
        // Clear API cache when new user logs in to prevent seeing previous user's data
        apiClient.invalidateCache();
        
        // Store authentication data (without tokens - they're in cookies)
        authPersistence.storeAuthData({
          access_token: '', // Tokens are in httpOnly cookies
          refresh_token: '', // Tokens are in httpOnly cookies
          token_type: 'Bearer',
          access_token_expires_at: '', // Will be updated from API if needed
          user: {
            ...userData,
            email: userData.email || '',
          },
          remember_me: action.payload.rememberMe || false,
          last_activity: new Date().toISOString(),
        });
        
        // Perform migration cleanup for existing users with old tokens
        performUserMigration();
      })
      .addCase(verifyAdminHostOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Verify Usher OTP
      .addCase(verifyUsherOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyUsherOTP.fulfilled, (state, action) => {
        state.loading = false;
        // Extract roles and active_role from response if available
        const userData = {
          ...action.payload.user,
          roles: action.payload.available_roles || [action.payload.user.role],
          active_role: action.payload.active_role || action.payload.user.role,
        };
        state.user = userData;
        // Tokens are now in httpOnly cookies, not in response body
        state.tokens = null;
        state.isAuthenticated = true;
        state.loginType = 'otp';
        state.rememberMe = action.payload.rememberMe || false;
        state.sessionChecked = true;
        state.error = null;
        
        // Clear API cache when new user logs in to prevent seeing previous user's data
        apiClient.invalidateCache();
        
        // Store authentication data (without tokens - they're in cookies)
        authPersistence.storeAuthData({
          access_token: '', // Tokens are in httpOnly cookies
          refresh_token: '', // Tokens are in httpOnly cookies
          token_type: 'Bearer',
          access_token_expires_at: '', // Will be updated from API if needed
          user: {
            ...userData,
            email: userData.email || '',
          },
          remember_me: action.payload.rememberMe || false,
          last_activity: new Date().toISOString(),
        });
        
        // Perform migration cleanup for existing users with old tokens
        performUserMigration();
      })
      .addCase(verifyUsherOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Check stored session
      .addCase(checkStoredSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkStoredSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.loginType = action.payload.loginType as 'email' | 'otp';
        state.rememberMe = action.payload.rememberMe;
        state.sessionChecked = true;
        state.error = null;
        
        // Clear API cache when session is restored to ensure fresh data
        apiClient.invalidateCache();
      })
      .addCase(checkStoredSession.rejected, (state, action) => {
        state.loading = false;
        state.sessionChecked = true;
        state.error = null; // Don't show error for failed session restore
        
        // Note: Tokens are now in httpOnly cookies, not in stored data
        // If verification failed, we cannot restore tokens from storage
        // Only restore user data (non-sensitive) if available
        const storedData = authPersistence.getStoredAuthData();
        
        if (storedData && storedData.user) {
          // Restore minimal user data only (non-sensitive information)
          // Email and phone are NOT stored in sessionStorage for security
          // They will be fetched from API after authentication verification
          // Authentication must come from cookies via API verification
          state.user = {
            id: storedData.user.id,
            email: undefined, // Will be fetched from API - not stored for security
            phone: undefined, // Will be fetched from API - not stored for security
            first_name: storedData.user.first_name,
            last_name: storedData.user.last_name,
            role: storedData.user.role as 'admin' | 'host' | 'guest' | 'usher',
            roles: storedData.user.roles || [storedData.user.role],
            active_role: storedData.user.active_role || storedData.user.role,
            is_active: storedData.user.is_active,
            created_at: new Date().toISOString(),
            preferred_language: 'en',
          };
          state.rememberMe = storedData.remember_me;
          // loginType will be determined from API response, not stored data
          // Note: isAuthenticated remains false - user must verify via API with cookies
          // Tokens are not restored - they're in httpOnly cookies
        } else {
          // No stored data - clear auth state
          state.isAuthenticated = false;
          state.user = null;
          state.tokens = null;
        }
      })
      
      // Auto refresh token
      .addCase(autoRefreshToken.fulfilled, (state, action) => {
        if (state.tokens) {
          state.tokens.access_token = action.payload.access_token;
          state.tokens.access_token_expires_at = action.payload.access_token_expires_at;
          if (action.payload.refresh_token) {
            state.tokens.refresh_token = action.payload.refresh_token;
          }
        }
      })
      .addCase(autoRefreshToken.rejected, (state) => {
        // If auto refresh fails, clear the session
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.loginType = null;
        state.rememberMe = false;
        state.error = null;
      })
      
      // Refresh token (legacy)
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (state.tokens) {
          state.tokens.access_token = action.payload.access_token;
          state.tokens.access_token_expires_at = action.payload.access_token_expires_at;
        }
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.loginType = null;
        state.error = null;
        state.loading = false;
        state.rememberMe = false;
        state.sessionChecked = true;
        
        // Clear persisted data
        authPersistence.clearAuthData();
        
        // Clear API cache to prevent showing stale data from previous user
        apiClient.invalidateCache();
      })
      .addCase(logout.rejected, (state) => {
        // Even if logout API fails, clear local state
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.loginType = null;
        state.error = null;
        state.loading = false;
        state.rememberMe = false;
        state.sessionChecked = true;
        
        // Clear persisted data
        authPersistence.clearAuthData();
        
        // Clear API cache to prevent showing stale data from previous user
        apiClient.invalidateCache();
      })
      
      // Fetch profile
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        // Update localStorage with verified user
        if (state.tokens) {
          authPersistence.storeAuthData({
            access_token: state.tokens.access_token,
            refresh_token: state.tokens.refresh_token,
            token_type: state.tokens.token_type,
            access_token_expires_at: state.tokens.access_token_expires_at,
            user: action.payload,
            remember_me: state.rememberMe,
            last_activity: new Date().toISOString(),
          });
        }
      })
      
      // Verify user data
      .addCase(verifyUserData.pending, (state) => {
        state.isVerifying = true;
        state.verificationFailed = false;
      })
      .addCase(verifyUserData.fulfilled, (state, action) => {
        const verifiedUser = action.payload;
        const currentUser = state.user;
        
        // Helper function to check if user data actually changed
        const hasUserDataChanged = (current: any, verified: any): boolean => {
          if (!current) return true; // No current user, definitely changed
          
          // Compare critical fields that would affect the app
          const fieldsToCompare = ['id', 'role', 'email', 'phone', 'first_name', 'last_name', 'is_active'];
          for (const field of fieldsToCompare) {
            if (current[field] !== verified[field]) {
              return true;
            }
          }
          return false;
        };
        
        // Check if role changed (critical for security)
        const roleChanged = currentUser && currentUser.role !== verifiedUser.role;
        const userDataChanged = hasUserDataChanged(currentUser, verifiedUser);
        
        // Always update verification state (necessary for UI)
        state.isVerifying = false;
        state.verificationFailed = false;
        
        // Only update lastVerified and user data if something actually changed
        // This prevents unnecessary state updates that trigger re-renders
        if (roleChanged) {
          const oldRole = currentUser.role;
          const newRole = verifiedUser.role;
          
          // Log security warning in development
          if (process.env.NODE_ENV === 'development') {
            logWarning(`Role mismatch detected! Stored: ${oldRole}, Backend: ${newRole}. Using backend role.`);
          }
          
          // Update user data (role changed - critical update)
          state.user = verifiedUser;
          state.lastVerified = new Date().toISOString();
          
          // Update localStorage with verified user
          if (state.tokens) {
            authPersistence.storeAuthData({
              access_token: state.tokens.access_token,
              refresh_token: state.tokens.refresh_token,
              token_type: state.tokens.token_type,
              access_token_expires_at: state.tokens.access_token_expires_at,
              user: verifiedUser,
              remember_me: state.rememberMe,
              last_activity: new Date().toISOString(),
            });
          }
        } else if (userDataChanged) {
          // Other user data changed (but not role) - update user
          state.user = verifiedUser;
          state.lastVerified = new Date().toISOString();
          
          // Update localStorage with verified user
          if (state.tokens) {
            authPersistence.storeAuthData({
              access_token: state.tokens.access_token,
              refresh_token: state.tokens.refresh_token,
              token_type: state.tokens.token_type,
              access_token_expires_at: state.tokens.access_token_expires_at,
              user: verifiedUser,
              remember_me: state.rememberMe,
              last_activity: new Date().toISOString(),
            });
          }
        } else {
          // No changes detected - completely silent verification
          // Don't update lastVerified (prevents unnecessary state updates)
          // Don't update user object reference (prevents re-renders)
          // Don't update localStorage (no changes to persist)
          // Only update isVerifying and verificationFailed (minimal necessary state)
          // This ensures verification runs silently in the background without causing reloads
        }
      })
      .addCase(verifyUserData.rejected, (state, action) => {
        state.isVerifying = false;
        
        // Handle specific error cases
        if (action.payload === 'UNAUTHORIZED' || action.payload === 'FORBIDDEN') {
          // User logged out or deactivated on backend - clear session
          state.user = null;
          state.tokens = null;
          state.isAuthenticated = false;
          state.loginType = null;
          state.rememberMe = false;
          authPersistence.clearAuthData();
        } else {
          // Network error or other issues - mark as failed but don't clear session
          state.verificationFailed = true;
          logWarning('User verification failed');
        }
      })
      
      // Request Password Reset OTP
      .addCase(requestPasswordResetOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPasswordResetOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestPasswordResetOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to request password reset';
      })
      
      // Verify Password Reset OTP Only
      .addCase(verifyPasswordResetOTPOnly.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPasswordResetOTPOnly.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(verifyPasswordResetOTPOnly.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to verify OTP';
      })
      
      // Verify Password Reset OTP and Reset Password
      .addCase(verifyPasswordResetOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPasswordResetOTP.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(verifyPasswordResetOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to reset password';
      });
  },
});

export const { clearError, clearAuth, setTokens, setRememberMe, updateLastActivity, setUser } = authSlice.actions;
export default authSlice.reducer;
